import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  useToast,
  Link,
  Card,
  CardBody,
  Text,
  Badge,
  HStack,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { api } from '../services/api';

interface Metadata {
  name: string;
  tagline: string;
  email: string;
  mobile: string;
  facebook_url: string;
  playstore_url: string;
  web_url: string;
  social_url: string;
}

const initialMetadata: Metadata = {
  name: '',
  tagline: '',
  email: '',
  mobile: '',
  facebook_url: '',
  playstore_url: '',
  web_url: '',
  social_url: '',
};

const AppMetadataPage = () => {
  const [metadata, setMetadata] = useState<Metadata>(initialMetadata);
  const [formData, setFormData] = useState<Metadata>(initialMetadata);
  const [errors, setErrors] = useState<Partial<Metadata>>({});
  const [loading, setLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const loadMetadata = async () => {
    try {
      setLoading(true);
      const data = await api.getMetadata();
      setMetadata(data);
    } catch (error) {
      console.error('Error loading metadata:', error);
      toast({
        title: 'Error',
        description: 'Failed to load app metadata',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMetadata();
  }, []);

  const validateForm = () => {
    const newErrors: Partial<Metadata> = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    // Mobile validation
    const mobileRegex = /^\d{10}$/;
    if (!mobileRegex.test(formData.mobile)) {
      newErrors.mobile = 'Mobile number must be 10 digits';
    }

    // URL validations
    const urlRegex = /^https?:\/\/[^\s$.?#].[^\s]*$/;
    if (formData.facebook_url && !urlRegex.test(formData.facebook_url)) {
      newErrors.facebook_url = 'Invalid Facebook URL';
    }
    if (formData.playstore_url && !urlRegex.test(formData.playstore_url)) {
      newErrors.playstore_url = 'Invalid Play Store URL';
    }
    if (formData.web_url && !urlRegex.test(formData.web_url)) {
      newErrors.web_url = 'Invalid Website URL';
    }
    if (formData.social_url && !urlRegex.test(formData.social_url)) {
      newErrors.social_url = 'Invalid Social Media URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEdit = () => {
    setFormData(metadata);
    onOpen();
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      await api.updateMetadata(formData);
      await loadMetadata();
      onClose();
      toast({
        title: 'Success',
        description: 'App metadata updated successfully',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error updating metadata:', error);
      toast({
        title: 'Error',
        description: 'Failed to update app metadata',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const renderLink = (url: string, type: string) => {
    if (!url) return <Text color="gray.500">Not set</Text>;
    return (
      <Link href={url} isExternal color="blue.500">
        {type === 'email' ? url : `View ${type}`} ↗️
      </Link>
    );
  };

  return (
    <VStack spacing={6} align="stretch">
      <HStack justify="space-between">
        <Heading size="xl">⚙️ App Metadata</Heading>
        <Button colorScheme="blue" onClick={handleEdit} isLoading={loading}>
          ✏️ Edit Metadata
        </Button>
      </HStack>

      <Card>
        <CardBody>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Field</Th>
                <Th>Value</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td fontWeight="bold">Name</Td>
                <Td>{metadata.name}</Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Tagline</Td>
                <Td>{metadata.tagline}</Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Email</Td>
                <Td>{renderLink(metadata.email, 'email')}</Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Mobile</Td>
                <Td>
                  <Link href={`tel:${metadata.mobile}`} color="blue.500">
                    {metadata.mobile}
                  </Link>
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Facebook</Td>
                <Td>{renderLink(metadata.facebook_url, 'Facebook')}</Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Play Store</Td>
                <Td>{renderLink(metadata.playstore_url, 'Play Store')}</Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Website</Td>
                <Td>{renderLink(metadata.web_url, 'Website')}</Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Social Media</Td>
                <Td>{renderLink(metadata.social_url, 'Social Media')}</Td>
              </Tr>
            </Tbody>
          </Table>
        </CardBody>
      </Card>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit App Metadata</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired isInvalid={!!errors.name}>
                <FormLabel>App Name</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter app name"
                />
                <FormErrorMessage>{errors.name}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>Tagline</FormLabel>
                <Input
                  value={formData.tagline}
                  onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                  placeholder="Enter tagline"
                />
              </FormControl>

              <FormControl isRequired isInvalid={!!errors.email}>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter email address"
                />
                <FormErrorMessage>{errors.email}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={!!errors.mobile}>
                <FormLabel>Mobile</FormLabel>
                <Input
                  type="tel"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  placeholder="Enter 10-digit mobile number"
                />
                <FormErrorMessage>{errors.mobile}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.facebook_url}>
                <FormLabel>Facebook URL</FormLabel>
                <Input
                  type="url"
                  value={formData.facebook_url}
                  onChange={(e) => setFormData({ ...formData, facebook_url: e.target.value })}
                  placeholder="Enter Facebook page URL"
                />
                <FormErrorMessage>{errors.facebook_url}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.playstore_url}>
                <FormLabel>Play Store URL</FormLabel>
                <Input
                  type="url"
                  value={formData.playstore_url}
                  onChange={(e) => setFormData({ ...formData, playstore_url: e.target.value })}
                  placeholder="Enter Play Store app URL"
                />
                <FormErrorMessage>{errors.playstore_url}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.web_url}>
                <FormLabel>Website URL</FormLabel>
                <Input
                  type="url"
                  value={formData.web_url}
                  onChange={(e) => setFormData({ ...formData, web_url: e.target.value })}
                  placeholder="Enter website URL"
                />
                <FormErrorMessage>{errors.web_url}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.social_url}>
                <FormLabel>Social Media URL</FormLabel>
                <Input
                  type="url"
                  value={formData.social_url}
                  onChange={(e) => setFormData({ ...formData, social_url: e.target.value })}
                  placeholder="Enter social media URL"
                />
                <FormErrorMessage>{errors.social_url}</FormErrorMessage>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSubmit} isLoading={loading}>
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default AppMetadataPage; 